import { z } from 'zod'
import { answerShape } from '../npo'
import { baseModelShape } from '../baseModel'
import { donorShape } from '../donor'
import { campaignShape } from '../campaign'
import { frequencyShape } from '../frequencies'
import { userShape } from '../users'

export const donationCreateShape = {
  amount: z.number().or(z.string()), //<-- response.contributionAmount
  donor: z.string(),
  campaign: z.string().uuid(),
  answers: z.array(z.string()).optional().nullable(),
  frequency: z.string().uuid().optional(),
  uniqueId: z.string().optional(),
  transactionId: z.string().optional(), // <-- response.submissionId
  consId: z.string().optional(),
  confirmationCode: z.string().optional(), // <-- response.confirmationID
  transactionErrorMsg: z.string().optional(),
  status: z.string().optional(),
  cardBrand: z.string().optional(),
  cardMaskedPan: z.string().optional(),
  cardExpirationDate: z.string().optional(),
  qrCode: z.boolean().optional(),
  cardHolderName: z.string().optional(),
}

export const donationShape = {
  ...baseModelShape,
  amount: z.number(),
  donor: z.string(),
  donorRef: z.object(donorShape),
  campaign: z.object(campaignShape),
  fundraiser: z.object(userShape),
  answerRefs: z.array(z.object(answerShape)),
  answers: z.array(z.string()),
  frequency: z.object(frequencyShape),
  uniqueId: z.string(),
  transactionId: z.string(),
  transactionErrorMsg: z.string(),
  consId: z.string(),
  confirmationCode: z.string(),
  status: z.string(),
  canResubmit: z.boolean().optional(),
  cardBrand: z.string(),
  qrCode: z.string(),
  cardHolderName: z.string(),
  notes: z.string().optional().nullable(),
  isDuplicate: z.boolean().optional(),
}

export const updateDonationNotesInput = {
  donationId: z.string().uuid(),
  notes: z.string(),
}

export const donationDuplicatesInput = {
  donationId: z.string().uuid(),
}

export const donationDuplicatesOutput = {
  matches: z.array(
    z.object({
      id: z.string(),
      givarooId: z.number().nullable().optional(),
      fields: z.array(z.string()).optional().default([]),
    }),
  ),
}
export const statusShape = {
  id: z.string(),
  name: z.string(),
}
export const frequencyIndexesShape = {
  id: z.string(),
  name: z.string(),
}
export const donationFilterValuesShape = {
  campaigns: z.object(campaignShape),
  frequencies: z.object(frequencyIndexesShape),
  statuses: z.object(statusShape),
  donorStatuses: z.object(statusShape),
}

export const polygonVertexShape = z.object({
  lat: z.number(),
  lng: z.number(),
})

export const donationFiltersShape = {
  ordering: z.string().optional(),
  campaign: z.string(),
  dateRangeStart: z.date().or(z.string()),
  dateRangeEnd: z.date().or(z.string()),
  fundraiser: z.string(),
  frequency: z.string(),
  status: z.string(),
  currentDonorStatus: z.string(),
  donor: z.string(),
  givarooId: z.string(),
  polygon: z.array(polygonVertexShape).optional().nullable(),
}

export const exportDonationTemplateShape = {
  fields: z.array(z.string()),
  ordering: z.string().optional(),
  campaign: z.string(),
  dateRangeStart: z.date().or(z.string()),
  dateRangeEnd: z.date().or(z.string()),
  fundraiser: z.string(),
  frequency: z.string(),
  status: z.string(),
  currentDonorStatus: z.string(),
  polygon: z.array(polygonVertexShape).optional().nullable(),
}

export const storeDonationTemplateShape = {
  fields: z.array(z.string()),
  ordering: z.string().optional(),
  campaign: z.string(),
  fundraiser: z.string(),
  frequency: z.string(),
  status: z.string(),
  currentDonorStatus: z.string(),
  name: z.string(),
  filterType: z.string(),
  autoExportEnabled: z.boolean().optional(),
  scheduleFrequency: z.enum(['daily', 'weekly']).nullable().optional(),
  scheduleWeekday: z.number().int().min(0).max(6).nullable().optional(),
  scheduleStartDate: z.string().nullable().optional(),
}

export const removeDonationTemplateShape = {
  templateId: z.string().uuid(),
}

export const IRCSpringBoardDonationInputShape = {
  firstName: z.string(),
  lastName: z.string(),
  mail: z.string().email(),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string().or(z.number()),
  cardNumber: z.string().or(z.number()),
  cardCvv: z.string().or(z.number()),
  amount: z.string().or(z.number()),
  frequency: z.string(),
  uniqueId: z.string().uuid(),
  cardExpDateMonth: z.number(),
  cardExpDateYear: z.number(),
  mobilePhone: z.string(),
  campaign: z.string().uuid(),
  frequencyId: z.string().uuid(),
  confirmationCode: z.string(),
  qrCode: z.boolean().optional(),
  longitude: z.string().or(z.number()).optional(),
  latitude: z.string().or(z.number()).optional(),
  isEmailPreferred: z.boolean().optional(),
  isSmsPreferred: z.boolean().optional(),
  isPhoneCallPreferred: z.boolean().optional(),
  dateOfBirth: z.string(),
  // Advisory only: the backend re-derives the fundraiser from the signed form grant
  // and overwrites whatever arrives here, so a QR device does not need to know it.
  fundraiserId: z.string().uuid().nullable().optional(),
  cardHolderName: z.string(),
  donorVerificationSms: z.boolean(),
}
export const IRCSpringBoardDonationOutputShape = {
  status: z.string(),
  submissionId: z.number(),
}

export const WWFEngaingNetworkDonationInputShape = {
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string(),
  mail: z.string().email(),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string().or(z.number()),
  cardNumber: z.string().or(z.number()),
  cardCvv: z.string().or(z.number()),
  amount: z.string().or(z.number()),
  frequency: z.string(),
  uniqueId: z.string().uuid(),
  cardExpDateMonth: z.number(),
  cardExpDateYear: z.number(),
  emailUpdates: z.string(),
  textMessages: z.string(),
  originSource: z.string(),
  campaignId: z.string().uuid(),
  cardHolderName: z.string(),
  mobilePhone: z.string(),
  homePhone: z.string(),
  frequencyId: z.string().uuid(),
  confirmationCode: z.string(),
  qrCode: z.boolean().optional(),
  longitude: z.string().or(z.number()),
  latitude: z.string().or(z.number()),
  isEmailPreferred: z.boolean().optional(),
  isSmsPreferred: z.boolean().optional(),
  isPhoneCallPreferred: z.boolean().optional(),
  // See IRCSpringBoardDonationInputShape: resolved server-side from the form grant.
  fundraiserId: z.string().uuid().nullable().optional(),
  donorVerificationSms: z.boolean(),
}
export const WWFEngaingNetworkDonationOutputShape = {
  status: z.string(),
  transactionId: z.number(),
  message: z.string(),
}
export const canvassingOrgAdminChartInputShape = {
  start_date: z.string(),
  end_date: z.string(),
  campaign_filter_value: z.string().optional(),
  fundraiser_filter_value: z.string().optional(),
}

export const donorSearchInputShape = z.object({
  query: z.string(),
  fundraiser: z.string().optional(),
  status: z.string().optional(),
  frequency: z.string().optional(),
  campaign: z.string().optional(),
})

export const donorSearchOutputShape = z.array(donorShape)

export const fundraiserSearchInputShape = z.object({
  query: z.string(),
})

export const fundraiserSearchOutputShape = z.array(userShape)

export const smsDonorForDonationVerificationInput = {
  mobilePhone: z.string(),
  url: z.string(),
  uniqueId: z.string(),
  donorName: z.string(),
  // Proves which fundraiser is asking for the text. Django mints the grant that goes
  // into the link itself, so nothing here decides what the donor receives.
  formToken: z.string().nullish(),
}

export const updateDonationVerificationStatusInput = {
  uniqueId: z.string(),
  verified: z.boolean(),
  // The grant from the link in the donor's text, their only credential on this page.
  formToken: z.string().nullish(),
  legacyFundraiser: z.string().nullish(),
}

export const givarooIdSearchInputShape = z.object({
  query: z.string(),
})

export const givarooIdSearchOutputShape = z.array(
  z.object({
    givaroo_id: z.number(),
  }),
)
